import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { FullscreenLayoutAreas, FullscreenLayoutTemplate } from '@src/assets/css/fullscreen.template.scss';
import { ModalRefResult, ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { NodeType, TreeLikeFlatNode, TreeLikeNode } from '@src/suite/common/data-models/search-results/search-results';
import { SUBSTORIES_VS_GUID, SYSTEM_VS_GUID } from '@src/suite/common/enums/constants';
import { AppName, SearchType } from '@src/suite/common/enums/enums';
import { NavigationDirection } from '@src/suite/common/enums/navigation-direction';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { ViewStateLayouts } from '@src/suite/common/enums/viewstate-layouts';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { BasicContentService } from '@src/suite/common/services/basic-content-service';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { MediaFullscreenComponent } from '@src/suite/common/st-full-screen-dialog/media-fullscreen/st-media-fullscreen';
import { StFullScreenDialogComponent } from '@src/suite/common/st-full-screen-dialog/st-full-screen-dialog';
import { CreateTopicFullScreenDialogComponent } from '@src/suite/common/st-full-screen-dialog/topic-full-screen/create-topic-full-screen/create-topic-full-screen';
import {
    FourthSidecarActions,
    FullscreenActions,
    MainSidecarActions,
    OpenFullscreenModalActions,
    SecondSidecarActions,
    SidecarEfffectActions,
    ThirdSidecarActions,
} from '@src/suite/common/store/fullscreen/actions/fullscreen.actions';
import { CloseModalAction, ItemLayoutState, SidecarMenu, SidecarStatesMap } from '@src/suite/common/store/fullscreen/models/fullscreen.models';
import {
    selectAllStoriesMap,
    selectCurrentFullscreenMediaUrl,
    selectCurrentRecord,
    selectCurrentRecordType,
    selectCurrentStoryIndex,
    selectCurrentStoryParentTopic,
    selectCurrentVisibleSidecar,
    selectFullscreenCurrentViewRecord,
    selectFullscreenMediaUrls,
    selectFullscreenRecordGuid,
    selectLayoutTemplate,
} from '@src/suite/common/store/fullscreen/selectors/fullscreen.selectors';
import {
    selectAllSidecarStates,
    selectSidecarAssignmentList,
    selectSidecarFourthCurrentAssignmentViewState,
    selectSidecarFourthCurrentStoryListSearchType,
    selectSidecarFourthCurrentStoryViewState,
    selectSidecarMainCurrentAssignmentViewState,
    selectSidecarMainCurrentStoryListSearchType,
    selectSidecarMainCurrentStoryViewState,
    selectSidecarSecondCurrentAssignmentViewState,
    selectSidecarSecondCurrentStoryListSearchType,
    selectSidecarSecondCurrentStoryViewState,
    selectSidecarSelectedAssignment,
    selectSidecarSelectedAssignmentIndex,
    selectSidecarThirdCurrentAssignmentViewState,
    selectSidecarThirdCurrentStoryListSearchType,
    selectSidecarThirdCurrentStoryViewState,
    selectStoryRecordToEdit,
} from '@src/suite/common/store/fullscreen/selectors/sidecar.selectors';
import { selectFieldDefsDict } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ListActions } from '@src/suite/common/store/list/list.actions';
import { selectCurrentNodeUuid, selectListNewItemNodes } from '@src/suite/common/store/list/list.selectors';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { selectCurrentApplication } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { selectCurrentGroupBy, selectCurrentViewState } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { selectTotalGroupedItem, selectTreeControl } from '@src/suite/common/store/playbook/playbook.selector';
import { AssignmentRecordActions, RundownRecordActions, StoriesRecordActions, TopicRecordActions } from '@src/suite/common/store/records/records.actions';
import {
    selectAssignmentRecordByGuid,
    selectRundownRecordsMap,
    selectRundownTemplatesMap,
    selectSubStoriesByParentGuid,
    selectTopicRecordsMap,
    selectTopicsWithAssignmentsAndStories,
} from '@src/suite/common/store/records/records.selectors';
import { StoryActions } from '@src/suite/common/store/story/actions/story.actions';
import { cleanupStoryFromSidecarActions, createTopicInGroup, findIfStoryVisibleInSidecar, initializeRecord } from '@src/suite/common/utils/record-utils';
import { navigateDirectly, navigateTo, navigateToImage, navigateToStory } from '@src/suite/common/utils/store-utils';
import { KeyName } from '@src/suite/common/utils/util';
import { getDatabasesOptions, isPublicationViewStateRecord } from '@src/suite/common/utils/viewstate-utils';
import { RundownService } from '@src/suite/live/services/rundown.service';
import { LiveApiActions } from '@src/suite/live/store/actions/live.actions';
import { selectLiveTreeControl } from '@src/suite/live/store/selectors/live.selectors';
import { PlaybookSearchService } from '@src/suite/playbook/services/playbook-search-service';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';
import { TopicService } from '@src/suite/playbook/services/topic-service';
import { catchError, debounceTime, exhaustMap, filter, forkJoin, from, map, merge, mergeMap, of, switchMap, take, tap } from 'rxjs';
import { v4 } from 'uuid';

import { PersonalSettingsService } from '../../../services/personal-settings-service';
import { HelloResultsActions } from '../../hello-results/hello-results.actions';
import { selectManageTemplatesTreeControl } from '../../templates/templates.selector';
import { selectItemLayoutState } from '../selectors/fullscreen.selectors';

@Injectable()
export class FullscreenModalEffects {
    private nomWebServicesJson = inject(NomWebServicesJson);
    private topicService = inject(TopicService);
    private dialog = inject(MatDialog);
    private actions$ = inject(Actions);
    private store = inject(Store);
    private playbookService = inject(PlaybookService);
    private playbookSearchService = inject(PlaybookSearchService);
    private rundownService = inject(RundownService);
    private basicContentService = inject(BasicContentService);
    private modalService = inject(ModalService);
    private personalSettingsService = inject(PersonalSettingsService);
    private globalTablesService = inject(GlobalTablesService);

    private dialogRef: MatDialogRef<StFullScreenDialogComponent, any> = null;
    // TODO: honestly I prefer to extend the one above
    private createTopicDialogRef: MatDialogRef<CreateTopicFullScreenDialogComponent, any> = null;

    closeRundownFullscreenModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.closeRundownFullscreenModal),
            concatLatestFrom(() => [this.store.select(selectCurrentRecordType)]),
            map(([_, currentRecordType]) => {
                if (currentRecordType === ObjectTypes.RUNDOWN) {
                    return OpenFullscreenModalActions.closeFullscreenModal();
                }
                return ApplicationActions.noOpAction();
            })
        );
    });

    closeFullscreenModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.closeFullscreenModal, OpenFullscreenModalActions.closeFullscreenModalForce),
            concatLatestFrom(() => [this.store.select(selectCurrentRecord), this.store.select(selectI18N)]),
            exhaustMap(([action, currentRecord, languageSettings]) => {
                const okActions = [];
                okActions.push(FullscreenActions.resetState());
                if (action.type === OpenFullscreenModalActions.closeFullscreenModal.type && currentRecord?.isNewRecord) {
                    const modalRef: ModalRefResult = this.modalService.openModal({
                        title: languageSettings.PSTWebConfirmCancelShort,
                        content: languageSettings.PSTWebConfirmCancel,
                    });

                    if (!modalRef) {
                        return of(ApplicationActions.noOpAction());
                    }
                    return modalRef.result.pipe(
                        take(1),
                        exhaustMap(modalRes => {
                            if (modalRes === ModalResponseCodes.OK) {
                                this.dialogRef?.close(CloseModalAction.CLOSE_MODAL);
                                this.createTopicDialogRef?.close(CloseModalAction.CLOSE_MODAL);
                                return okActions;
                            } else if (modalRes === ModalResponseCodes.DISCARD) {
                                this.dialogRef?.close(CloseModalAction.CLOSE_MODAL);
                                this.createTopicDialogRef?.close(CloseModalAction.CLOSE_MODAL);
                                return of(FullscreenActions.resetState());
                            }
                            return [ApplicationActions.noOpAction()];
                        }),
                        catchError(() => {
                            return [ApplicationActions.noOpAction()];
                        })
                    );
                }
                this.createTopicDialogRef?.close(CloseModalAction.CLOSE_MODAL);
                this.dialogRef?.close(CloseModalAction.CLOSE_MODAL);
                return okActions;
            })
        );
    });

    openTopicFullscreenModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.openTopicFullscreenDialog, FullscreenActions.openTopic),
            concatLatestFrom(() => [this.store.select(selectCurrentRecord)]),
            switchMap(([action, currentRecord]) => {
                const topicsMap = this.store.selectSignal(selectTopicsWithAssignmentsAndStories);
                const storeTopic = topicsMap()?.get(action.recordGuid);
                const record = { ...(action.topicRecord || storeTopic) };
                const loadTopicData = async (topic: StorytellingTopicRecord, isMetadata: boolean, currentNewRecord: StorytellingTopicRecord) => {
                    if (currentNewRecord?.isNewRecord) {
                        return { ...currentNewRecord };
                    }

                    if (isMetadata) {
                        // TODO Handle record RTU and Store registration
                        const recordRes = await this.topicService.getTopicContent(this.nomWebServicesJson.getDatabase(), topic.Path.fullPath, topic.Guid);
                        topic = recordRes.data;
                    }
                    if (record?.Type === ObjectTypes.TOPIC && topic?.Path?.fullPath && !action?.isCreateAndNext) {
                        // TODO Handle record RTU and Store registration
                        const storiesRes = await this.topicService.getTopicStories(topic.Database, topic?.Path?.fullPath?.substring(0, topic?.Path?.fullPath?.lastIndexOf('\\')), topic?.Guid);
                        topic.stories = storiesRes.data;
                    }
                    return topic;
                };

                return from(loadTopicData(record, action.isMetadata, currentRecord)).pipe(
                    exhaustMap(rec => {
                        const actions = [];
                        if (rec?.isNewRecord) {
                            return of(FullscreenActions.updateCurrentNewRecord({ record: rec }));
                        }
                        if (action.isMetadata) {
                            actions.push(TopicRecordActions.updateTopics({ records: [rec] }));
                        }
                        if (action.type === OpenFullscreenModalActions.openTopicFullscreenDialog.type) {
                            actions.push(OpenFullscreenModalActions.openFullscreenModal({ hideNavigation: action.hideNavigation }));
                        }
                        if (rec?.stories?.length > 0) {
                            actions.push(StoriesRecordActions.updateStories({ records: rec.stories }));
                        }
                        if (rec?.children?.length > 0) {
                            actions.push(AssignmentRecordActions.updateAssignments({ records: rec.children }));
                        }
                        if (action.currentNodeUUID) {
                            actions.push(ListActions.navigateObject({ nodeUuid: action.currentNodeUUID }));
                        }
                        if (actions.length > 0) {
                            return actions;
                        }
                        return of(ApplicationActions.noOpAction());
                    })
                );
            })
        );
    });

    openRundownFullscreenModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                OpenFullscreenModalActions.openRundownFullscreenDialog,
                OpenFullscreenModalActions.openRundownTemplateFullscreenDialog,
                FullscreenActions.openRundown,
                FullscreenActions.openRundownTemplate
            ),
            switchMap(action => {
                let storeRecord: StorytellingRundownRecord;
                const isTemplate = action.type === OpenFullscreenModalActions.openRundownTemplateFullscreenDialog.type || action.type === FullscreenActions.openRundownTemplate.type;

                if (isTemplate) {
                    const rundownTemplates = this.store.selectSignal(selectRundownTemplatesMap)();
                    storeRecord = rundownTemplates?.get(action.recordGuid);
                } else {
                    const rundowns = this.store.selectSignal(selectRundownRecordsMap)();
                    storeRecord = rundowns?.get(action.recordGuid);
                }
                return this.rundownService.getRundown(storeRecord.Database, storeRecord.Path.fullPath, storeRecord.Guid).pipe(
                    switchMap(res => {
                        if (res.status) {
                            const actions = [];
                            // when opening rundown it could be a template RO from manage templates or it could be from the list of rundowns/templates in the navigation, if it's from manage templates we want to update template in the store
                            if (isTemplate) {
                                actions.push(RundownRecordActions.updateRundownTemplates({ templates: [res.data] }));
                            } else {
                                actions.push(RundownRecordActions.updateRundowns({ records: [res.data] }));
                            }
                            actions.push(StoriesRecordActions.updateStories({ records: res.data?.children }));
                            if (action.currentNodeUUID) {
                                actions.push(ListActions.navigateObject({ nodeUuid: action.currentNodeUUID }));
                            }
                            if (action.type === OpenFullscreenModalActions.openRundownFullscreenDialog.type || action.type === OpenFullscreenModalActions.openRundownTemplateFullscreenDialog.type) {
                                actions.push(OpenFullscreenModalActions.openFullscreenModal({}));
                            }
                            return actions;
                        }
                        //TODO: We should be throwing errors from service methods
                        return of(ApplicationActions.noOpAction());
                    })
                );
            })
        );
    });

    openFullscreenModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.openFullscreenModal, OpenFullscreenModalActions.openCreateNewRecordFullscreen),
            filter(
                action =>
                    action.type !== OpenFullscreenModalActions.openCreateNewRecordFullscreen.type ||
                    (action.recordTemplateGuid && action.recordTemplateGuid.length > 0) ||
                    action.record?.Type !== ObjectTypes.TOPIC
            ),
            switchMap(action => {
                this.dialog.closeAll();
                return this.dialog.afterAllClosed.pipe(
                    take(1),
                    switchMap(() => {
                        this.dialogRef = this.dialog.open(StFullScreenDialogComponent, {
                            hasBackdrop: false,
                            disableClose: true,
                            autoFocus: true,
                            panelClass: 'st-topic-full-screen-panel',
                            data: {
                                hideNavigation: action.type === OpenFullscreenModalActions.openFullscreenModal.type ? action.hideNavigation : false,
                            },
                        });
                        const backdropObs = this.dialogRef.backdropClick().pipe(
                            tap(event => {
                                event.preventDefault();
                            }),
                            map(() => OpenFullscreenModalActions.closeFullscreenModal())
                        );
                        const keydownObs = this.dialogRef.keydownEvents().pipe(
                            filter((event: KeyboardEvent) => event.key === 'Escape'),
                            tap(event => {
                                event.preventDefault();
                            }),
                            filter(_ => {
                                const dropdown = document.querySelector('.wj-dropdown-panel');
                                return !dropdown;
                            }),
                            map(() => OpenFullscreenModalActions.closeFullscreenModal())
                        );
                        return merge(keydownObs, backdropObs);
                    })
                );
            })
        );
    });

    openCreateTopicModal$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.openCreateNewRecordFullscreen),
            filter(action => action.record?.Type === ObjectTypes.TOPIC && (!action?.recordTemplateGuid || action?.recordTemplateGuid.length === 0)),
            switchMap(_ => {
                this.dialog.closeAll();
                const width = '58.438rem';
                //When we create a topic from the story which is already on fullscreen we need that check
                const topic = this.store.selectSignal<StorytellingTopicRecord>(selectCurrentRecord)();
                this.createTopicDialogRef = this.dialog.open(CreateTopicFullScreenDialogComponent, {
                    id: 'create-topic-full-screen',
                    width,
                    maxHeight: '90vh',
                    hasBackdrop: true,
                    disableClose: true,
                    autoFocus: true,
                    data: {
                        topic,
                    },
                });
                const backdropObs = this.createTopicDialogRef.backdropClick().pipe(
                    tap(event => {
                        event.preventDefault();
                    }),
                    map(() => OpenFullscreenModalActions.closeFullscreenModal())
                );
                const keydownObs = this.createTopicDialogRef.keydownEvents().pipe(
                    filter((event: KeyboardEvent) => event.key === KeyName.Escape),
                    tap(event => {
                        event.preventDefault();
                    }),
                    map(() => OpenFullscreenModalActions.closeFullscreenModal())
                );
                return merge(keydownObs, backdropObs);
            })
        );
    });

    loadRundownStoredLayouts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.openFullscreenModal, OpenFullscreenModalActions.openCreateNewRecordFullscreen),
            map(_ => {
                return LiveApiActions.loadRundownStoredLayouts();
            })
        );
    });

    onAssignmentItemClicked$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.openAssignmentFullscreenDialog),
            concatLatestFrom(action => [this.store.select(selectAssignmentRecordByGuid(action.recordGuid)), this.store.select(selectTreeControl)]),
            switchMap(([action, storeRecord, treeControl]) => {
                const actions = [];
                // Use the currentNodeUUID from the caller if available (includes group prefix for correct position),
                // otherwise find the assignment node in the tree (may match the first occurrence in multi-group scenarios)
                const assignmentNode =
                    treeControl?.dataNodes?.find(node => node.uniqueId === action.currentNodeUUID) ||
                    treeControl?.dataNodes?.find(node => node.nodeType === NodeType.RECORD && (node.object as StorytellingRecord)?.Guid === action.recordGuid);
                const nodeUUID = assignmentNode?.uniqueId || action.currentNodeUUID || action.recordGuid;
                const topicGuid = storeRecord.ParentGuid || storeRecord.Path?.parentCollectionGuid;
                actions.push(
                    OpenFullscreenModalActions.openTopicFullscreenDialog({
                        recordType: ObjectTypes.TOPIC,
                        recordGuid: topicGuid,
                        isMetadata: action.isMetadata,
                        currentNodeUUID: nodeUUID,
                    })
                );
                actions.push(FullscreenActions.setFullscreenTemplate({ template: FullscreenLayoutTemplate.BASIC }));

                switch (action.sidecarMenu) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        actions.push(MainSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        actions.push(MainSidecarActions.selectSidecarMenu({ menu: SidecarMenu.ASSIGNMENTS }));
                        break;
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        actions.push(SecondSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        actions.push(SecondSidecarActions.selectSidecarMenu({ menu: SidecarMenu.ASSIGNMENTS }));
                        break;
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        actions.push(ThirdSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        actions.push(ThirdSidecarActions.selectSidecarMenu({ menu: SidecarMenu.ASSIGNMENTS }));
                        break;
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        actions.push(FourthSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        actions.push(FourthSidecarActions.selectSidecarMenu({ menu: SidecarMenu.ASSIGNMENTS }));
                        break;
                }
                return actions;
            })
        );
    });

    openHomeItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.openHomeItem),
            map(() => {
                const currentListRecord = this.store.selectSignal(selectFullscreenCurrentViewRecord);
                const recordGuid = this.store.selectSignal(selectFullscreenRecordGuid);
                switch (currentListRecord()?.Type) {
                    case ObjectTypes.TOPIC:
                        return FullscreenActions.openTopic({ recordGuid: currentListRecord()?.Guid, recordType: currentListRecord()?.Type, fromPinnedItem: false, standaloneStory: false });
                    case ObjectTypes.RUNDOWN:
                        return FullscreenActions.openRundown({ recordGuid: currentListRecord()?.Guid, recordType: currentListRecord()?.Type, fromPinnedItem: false, standaloneStory: false });
                    case ObjectTypes.SCRIPT:
                        if (currentListRecord()?.Guid !== recordGuid()) {
                            return FullscreenActions.openStory({ recordGuid: currentListRecord()?.Guid, recordType: currentListRecord()?.Type, fromPinnedItem: false, standaloneStory: true });
                        }
                        return ApplicationActions.noOpAction();
                    default:
                        return ApplicationActions.noOpAction();
                }
            })
        );
    });

    openAssignment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.openAssignment),
            concatLatestFrom(() => [this.store.select(selectLayoutTemplate)]),
            exhaustMap(([action, layout]) => {
                const actions = [];
                switch (action.sidecarMenu) {
                    case FullscreenLayoutAreas.SIDECAR_MAIN:
                        actions.push(MainSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        if (action.vsGuid) {
                            actions.push(MainSidecarActions.setCurrentAssignmentViewState({ guid: action.vsGuid }));
                        }
                        break;
                    case FullscreenLayoutAreas.SIDECAR_SECOND:
                        actions.push(SecondSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        if (action.vsGuid) {
                            actions.push(SecondSidecarActions.setCurrentAssignmentViewState({ guid: action.vsGuid }));
                        }
                        break;
                    case FullscreenLayoutAreas.SIDECAR_THIRD:
                        actions.push(ThirdSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        if (action.vsGuid) {
                            actions.push(ThirdSidecarActions.setCurrentAssignmentViewState({ guid: action.vsGuid }));
                        }
                        break;
                    case FullscreenLayoutAreas.SIDECAR_FOURTH:
                        actions.push(FourthSidecarActions.selectSidecarAssignment({ assignmentGuid: action.recordGuid }));
                        if (action.vsGuid) {
                            actions.push(FourthSidecarActions.setCurrentAssignmentViewState({ guid: action.vsGuid }));
                        }
                        break;
                }
                if (actions.length > 0 && layout === FullscreenLayoutTemplate.ONLY_MAIN) {
                    actions.push(FullscreenActions.setFullscreenTemplate({ template: FullscreenLayoutTemplate.BASIC }));
                }

                if (actions.length > 0) {
                    return actions;
                }
                return of(ApplicationActions.noOpAction());
            })
        );
    });

    openDiscoverItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.openDiscoverItem),
            concatLatestFrom(() => [this.store.select(selectLayoutTemplate)]),
            exhaustMap(([action, layout]) => {
                const actions = [];
                actions.push(MainSidecarActions.selectSidecarDiscoverItem({ guid: action.recordGuid }));
                if (layout === FullscreenLayoutTemplate.ONLY_MAIN) {
                    actions.push(FullscreenActions.setFullscreenTemplate({ template: FullscreenLayoutTemplate.BASIC }));
                }
                return actions;
            })
        );
    });

    onInitCreateNewTopic$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.initCreateNewRecordFullscreen),
            concatLatestFrom(() => [this.store.select(selectCurrentGroupBy), this.store.select(selectFieldDefsDict)]),
            switchMap(async ([action, groupBy, fieldDefs]) => {
                let newRecord = initializeRecord(action.recordType);
                switch (action.recordType) {
                    case ObjectTypes.TOPIC: {
                        if (action.recordTemplate) {
                            newRecord = await this.playbookService.initializeTopicFromTemplate(action.recordTemplate.Database, action.recordTemplate.Path.fullPath, action.recordTemplate.Guid);
                        } else if (action.wireRecord) {
                            newRecord = this.playbookService.initializeTopicFromWire(action.wireRecord);
                        } else if (action.baseRecord && action.baseRecord.Type === ObjectTypes.SCRIPT) {
                            const storiesMap = this.store.selectSignal(selectAllStoriesMap);
                            const storyRecord = storiesMap()?.get(action.baseRecord.Guid);
                            if (storyRecord) {
                                newRecord = this.playbookService.initializeTopicFromStory(storyRecord);
                            } else {
                                this.playbookService.initializeNewTopicRecordV2(newRecord);
                            }
                        } else {
                            this.playbookService.initializeNewTopicRecordV2(newRecord);
                        }
                        if (action.groupData) {
                            const groupByField = fieldDefs[groupBy];
                            createTopicInGroup(newRecord, groupByField, action.groupData);
                        }
                        break;
                    }
                    case ObjectTypes.SCRIPT: {
                        if (action.recordTemplate) {
                            newRecord = await this.playbookService.initializeStoryFromTemplate(action.recordTemplate.Database, action.recordTemplate.Path.fullPath, action.recordTemplate.Guid);
                        } else {
                            this.playbookService.initializeNewStoryRecordV2(newRecord);
                        }
                        newRecord.ModBy = this.nomWebServicesJson.UserId;
                        // TODO: not sure we need to create story in group
                        break;
                    }
                }
                newRecord.Guid = v4();
                newRecord.isNewRecord = true;

                return OpenFullscreenModalActions.openCreateNewRecordFullscreen({
                    record: newRecord,
                    selectedStoryIndex: -1,
                    recordTemplateGuid: action.recordTemplate?.Guid,
                    createInGroupData: action.groupData,
                    recordCreating: false,
                    wireRecord: action.wireRecord,
                    baseRecord: action.baseRecord,
                    isFromTemplate: !!action.recordTemplate,
                });
            })
        );
    });

    onOpenStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.openStory, OpenFullscreenModalActions.openStoryFullscreenDialog),
            concatLatestFrom(() => [this.store.select(selectCurrentRecord)]),
            switchMap(([action, currentStory]) => {
                if (!currentStory) return of(ApplicationActions.noOpAction());
                if (currentStory?.isNewRecord) {
                    return of(ApplicationActions.noOpAction());
                }

                const loadTopicStories = async (story: StorytellingStoryRecord) => {
                    if (action.type === OpenFullscreenModalActions.openStoryFullscreenDialog.type && story.PSTStoryTopicGUID) {
                        const topics = this.store.selectSignal(selectTopicRecordsMap)();
                        const topic = topics.get(story.PSTStoryTopicGUID);
                        if (!topic) {
                            return [];
                        }
                        const storiesRes = await this.topicService.getTopicStories(topic.Database, topic?.Path?.fullPath?.substring(0, topic?.Path?.fullPath?.lastIndexOf('\\')), topic?.Guid);
                        // Return [] on failure — storiesRes.data is an error string when status is false,
                        // and passing a string to stories.filter() below would throw silently.
                        return storiesRes.status ? storiesRes.data : [];
                    }
                    return [];
                };

                return from(loadTopicStories(currentStory)).pipe(
                    switchMap(stories => {
                        return from(
                            this.basicContentService.getRecord<StorytellingStoryRecord>({
                                database: currentStory?.Database || this.nomWebServicesJson.getDatabase(),
                                path: currentStory.Path?.fullPath,
                                guid: currentStory.Guid,
                            })
                        ).pipe(
                            switchMap(res => {
                                if (res.status) {
                                    const actions = [];
                                    stories = stories.filter(s => s.Guid !== res.data.Guid);
                                    stories.push(res.data);
                                    actions.push(
                                        StoriesRecordActions.updateStories({
                                            records: stories.map(s => ({
                                                ...s,
                                                loadedBasicContent: true,
                                            })),
                                        })
                                    );
                                    if (action.type === OpenFullscreenModalActions.openStoryFullscreenDialog.type) {
                                        actions.push(OpenFullscreenModalActions.openFullscreenModal({}));
                                    }
                                    if (action.currentNodeUUID) {
                                        actions.push(ListActions.navigateObject({ nodeUuid: action.currentNodeUUID }));
                                    }
                                    // Sync the sidecar substory list to the story now open in fullscreen.
                                    // parentGuid: the story itself, or its primary story if it's a substory.
                                    const currentSidecar = this.store.selectSignal(selectCurrentVisibleSidecar)();
                                    if (currentSidecar) {
                                        const parentGuid = res.data.PSTPrimaryStoryGUID || res.data.Guid;
                                        actions.push(SidecarEfffectActions.setCurrentSubStories({ sidecarType: currentSidecar, parentGuid }));
                                    }
                                    actions.push(FullscreenActions.setLoadingProgressBar({ showLoader: false }));
                                    return of(...actions);
                                }
                                return of(FullscreenActions.setLoadingProgressBar({ showLoader: false }));
                            }),
                            catchError(() => of(FullscreenActions.setLoadingProgressBar({ showLoader: false })))
                        );
                    })
                );
            })
        );
    });

    focusEditorIfStoryAlreadyOpened$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.tryOpenStory),
            switchMap(action => {
                const sidecarStates = this.store.selectSignal(selectAllSidecarStates)();
                const currentTemplate = this.store.selectSignal(selectLayoutTemplate)();
                //We concider the sidecar is open either if the sidecar is in the current layout and the specific sidecar tab is opened
                const result = findIfStoryVisibleInSidecar(sidecarStates, action.recordGuid, currentTemplate);
                let sidecar: string | undefined;

                if (result) {
                    sidecar = result[0];
                }

                const isDesiredStoryVisible = !!sidecar;
                if (!isDesiredStoryVisible) {
                    const actions: any[] = [];
                    actions.push(...cleanupStoryFromSidecarActions(sidecarStates, action.recordGuid));
                    actions.push(
                        FullscreenActions.openStory({ recordGuid: action.recordGuid, recordType: action.recordType, fromPinnedItem: action.fromPinnedItem, standaloneStory: action.standaloneStory })
                    );
                    return of(...actions);
                }
                // If story is already opened in visible sidecar, focus story editor
                return of(StoryActions.focusEditor({ storyGuid: action.recordGuid }));
            })
        );
    });

    onCloseCurrentStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.closeCurrentStory),
            concatLatestFrom(() => [this.store.select(selectStoryRecordToEdit)]),
            switchMap(([_, currentStory]) => {
                const actions: Action[] = [];
                actions.push(FullscreenActions.openTopic({ recordGuid: currentStory?.PSTStoryTopicGUID, recordType: ObjectTypes.TOPIC }));
                const allStates = this.store.selectSignal(selectAllSidecarStates)();
                for (const [key, state] of Object.entries(allStates)) {
                    if (state?.currentStoryViewStateGuid === SUBSTORIES_VS_GUID) {
                        actions.push(SidecarEfffectActions.setCurrentSubStories({ sidecarType: key as FullscreenLayoutAreas, parentGuid: null }));
                    }
                }
                return of(...actions);
            })
        );
    });

    onNavigateAssignment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.navigateAssignment),
            concatLatestFrom(action => {
                return [this.store.select(selectSidecarAssignmentList(action.sidecarMenu)), this.store.select(selectSidecarSelectedAssignmentIndex(action.sidecarMenu))];
            }),
            map(([action, assignmentList, selectedIdx]) => {
                if (selectedIdx == null || selectedIdx < 0 || assignmentList?.length === 0 || selectedIdx >= assignmentList.length) {
                    return ApplicationActions.noOpAction();
                }
                let assignmentGuid = assignmentList[selectedIdx];
                switch (action.direction) {
                    case NavigationDirection.PREVIOUS:
                        if (selectedIdx === 0) {
                            assignmentGuid = assignmentList[assignmentList.length - 1];
                        } else {
                            assignmentGuid = assignmentList[selectedIdx - 1];
                        }
                        break;
                    case NavigationDirection.NEXT:
                        if (selectedIdx === assignmentList.length - 1) {
                            assignmentGuid = assignmentList[0];
                        } else {
                            assignmentGuid = assignmentList[selectedIdx + 1];
                        }
                        break;
                    case NavigationDirection.FIRST:
                        assignmentGuid = assignmentList[0];
                        break;
                    case NavigationDirection.LAST:
                        assignmentGuid = assignmentList[assignmentList.length - 1];
                        break;
                }

                return FullscreenActions.openAssignment({
                    recordGuid: assignmentGuid,
                    recordType: ObjectTypes.ASSIGNMENT,
                    sidecarMenu: action.sidecarMenu,
                });
            })
        );
    });

    onSearchTermChanged$ = createEffect(() => {
        return this.actions$.pipe(
            debounceTime(300),
            ofType(FullscreenActions.setSearchTerm),
            switchMap(({ searchTerm }) => {
                if (!searchTerm) {
                    return of(FullscreenActions.setSearchResultsGuids({ guids: [] }));
                }
                const vs: StorytellingSearchViewStateRecord = {
                    PSTViewStateType: ViewStateTypes.SEARCH,
                    PSTViewStateSearchQuery: searchTerm,
                };
                const databases = getDatabasesOptions(
                    vs as StorytellingSingleViewStateRecord,
                    this.nomWebServicesJson.PSTServer,
                    this.nomWebServicesJson.PSTGroup,
                    this.globalTablesService.AllGroups,
                    this.globalTablesService.AllGroupsArray
                );
                if (databases.length === 0) {
                    return of(FullscreenActions.setSearchResultsGuids({ guids: [] }));
                }
                return forkJoin(
                    databases.map(database => {
                        return this.playbookService.loadPlaybookObjectsObservable(vs, null, database);
                    })
                ).pipe(
                    exhaustMap(response => {
                        let rundowns: StorytellingRundownRecord[] = [];
                        let topics: StorytellingTopicRecord[] = [];
                        let assignments: StorytellingAssignmentRecord[] = [];
                        let stories: StorytellingStoryRecord[] = [];
                        let listGuids: string[] = [];

                        response.forEach(res => {
                            if (res.status) {
                                rundowns = [...rundowns, ...res.data.rundowns.values()];
                                topics = [...topics, ...res.data.topics.values()];
                                assignments = [...assignments, ...res.data.assignments.values()];
                                stories = [...stories, ...res.data.stories.values()];
                                listGuids = [...listGuids, ...res.data.listGuids];
                            }
                        });
                        const actions = [];
                        actions.push(FullscreenActions.setSearchResultsGuids({ guids: [...(listGuids || [])] }));
                        if (rundowns.length > 0) actions.push(RundownRecordActions.updateRundowns({ records: [...rundowns] }));
                        if (topics.length > 0) actions.push(TopicRecordActions.updateTopics({ records: [...topics] }));
                        if (assignments.length > 0) actions.push(AssignmentRecordActions.updateAssignments({ records: [...assignments] }));
                        if (stories.length > 0) actions.push(StoriesRecordActions.updateStories({ records: [...stories] }));
                        return actions;
                    })
                );
            })
        );
    });

    navigateToRecord$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.navigateRecord),
            concatLatestFrom(() => [
                this.store.select(selectCurrentNodeUuid),
                this.store.select(selectTreeControl),
                this.store.select(selectListNewItemNodes),
                this.store.select(selectTotalGroupedItem),
            ]),
            switchMap(([action, currentNodeUUID, playbookTreecontrol, newItemNodes, totalGroupedItems]) => {
                const sidecarStates = this.store.selectSignal(selectAllSidecarStates)();
                const currentTemplate = this.store.selectSignal(selectLayoutTemplate)();
                const fullscreenRecordGuid = this.store.selectSignal(selectFullscreenRecordGuid)();
                if (!action.throughTopicStories) {
                    const vs = this.store.selectSignal(selectCurrentViewState)();
                    let candidates: {
                        item: TreeLikeFlatNode;
                        index: number;
                    }[] = [];

                    if (vs?.PSTViewStateLayout?.Value === ViewStateLayouts.GRID) {
                        let filteredIndex = 0;
                        for (const item of totalGroupedItems || []) {
                            if (item.nodeType === NodeType.RECORD) {
                                candidates.push({
                                    item,
                                    index: filteredIndex++,
                                });
                            } else {
                                candidates.push({
                                    item,
                                    index: filteredIndex++,
                                });
                            }
                        }
                    } else {
                        const isNewRecord = newItemNodes?.some(item => item.object.Guid === fullscreenRecordGuid);
                        if (isNewRecord) {
                            candidates = newItemNodes.map((item, index) => ({ item, index }));
                        } else {
                            const liveTreeControl = this.store.selectSignal(selectLiveTreeControl)();
                            const manageTemplatesTreeControl = this.store.selectSignal(selectManageTemplatesTreeControl)();
                            const currentApplication = this.store.selectSignal(selectCurrentApplication)();
                            const data = (() => {
                                switch (currentApplication) {
                                    case AppName.LIVE:
                                        return liveTreeControl;
                                    case AppName.TEMPLATES:
                                        return manageTemplatesTreeControl;
                                    default:
                                        return playbookTreecontrol;
                                }
                            })();
                            //We need that loop to match the indexed with the records when we remove unnecessary nodes
                            const recordNodes = data?.dataNodes?.filter(node => node.nodeType === NodeType.RECORD) || [];
                            candidates = recordNodes.map((item, index) => ({ item, index }));
                        }
                    }

                    const nextItem = navigateTo(action.direction, currentNodeUUID, candidates, sidecarStates, currentTemplate);
                    return this.goToItem(nextItem);
                } else {
                    const currentRecord = this.store.selectSignal(selectCurrentRecord)() as StorytellingStoryRecord;
                    let stories: StorytellingStoryRecord[];
                    let currentStoryIndex: number;

                    if (currentRecord?.PSTPrimaryStoryGUID) {
                        // Current record is a sub-story — navigate through sibling sub-stories
                        const subStoriesMap = this.store.selectSignal(selectSubStoriesByParentGuid)();
                        stories = subStoriesMap?.get(currentRecord.PSTPrimaryStoryGUID) || [];
                        currentStoryIndex = stories.findIndex(s => s.Guid === currentRecord.Guid);
                    } else {
                        const topic = this.store.selectSignal(selectCurrentStoryParentTopic)();
                        stories = topic?.stories || [];
                        currentStoryIndex = this.store.selectSignal(selectCurrentStoryIndex)();
                    }

                    if (currentStoryIndex === -1 || !stories.length) {
                        return of(ApplicationActions.noOpAction());
                    }
                    const newItem = navigateToStory(action.direction, currentStoryIndex, stories, sidecarStates, currentTemplate, fullscreenRecordGuid);
                    if (!newItem) {
                        return of(ApplicationActions.noOpAction());
                    }
                    // NOTE: we can use directly FullscreenActions.openStory({ recordGuid: newItem?.Guid, recordType: ObjectTypes.SCRIPT }))
                    // because we have already found which story is not opened in any sidecar
                    return of(
                        FullscreenActions.tryOpenStory({
                            recordGuid: newItem?.Guid,
                            recordType: ObjectTypes.SCRIPT,
                            fromPinnedItem: false,
                        })
                    );
                }
            })
        );
    });

    navigateRecordDirectly$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(FullscreenActions.navigateRecordDirectly),
            concatLatestFrom(() => [this.store.select(selectTreeControl), this.store.select(selectListNewItemNodes), this.store.select(selectFullscreenRecordGuid)]),
            exhaustMap(([action, playbookTreecontrol, newItemNodes, fullscreenRecordGuid]) => {
                if (!action.throughTopicStories) {
                    const isNewRecord = newItemNodes?.some(item => item.object.Guid === fullscreenRecordGuid);
                    let node: TreeLikeFlatNode = null;
                    if (isNewRecord) {
                        node = newItemNodes.find(item => action.uniqueNodeId.includes(item.uniqueId));
                    } else {
                        const currentApplication = this.store.selectSignal(selectCurrentApplication)();
                        const liveTreeControl = this.store.selectSignal(selectLiveTreeControl)();
                        const manageTemplatesTreeControl = this.store.selectSignal(selectManageTemplatesTreeControl)();
                        const treeControl = currentApplication === AppName.LIVE ? liveTreeControl : currentApplication === AppName.TEMPLATES ? manageTemplatesTreeControl : playbookTreecontrol;
                        node = navigateDirectly(action.uniqueNodeId, treeControl);
                    }
                    return this.goToItem(node);
                } else {
                    return of(FullscreenActions.tryOpenStory({ recordGuid: action.uniqueNodeId, recordType: ObjectTypes.SCRIPT }));
                }
            })
        );
    });

    goToItem(nextItem: TreeLikeFlatNode | TreeLikeNode) {
        const vs = this.store.selectSignal(selectCurrentViewState)();
        const localNavigateToRecord = (item: TreeLikeFlatNode | TreeLikeNode) => {
            let nextItemRecord = item?.object as StorytellingRecord;
            if (isPublicationViewStateRecord(vs) && nextItemRecord?.linkedRecords?.length > 0) {
                nextItemRecord = nextItemRecord.linkedRecords[0];
            }
            if (nextItemRecord) {
                const actions = [];
                actions.push(ListActions.navigateObject({ nodeUuid: item.uniqueId }));
                const currentTemplate = this.store.selectSignal(selectLayoutTemplate)();
                const isMetadataTopicNavigation = item?.nodeType === NodeType.METADATA;
                const sidecar = this.store.selectSignal(selectCurrentVisibleSidecar)();
                const selectedAssignment = this.store.selectSignal(selectSidecarSelectedAssignment(sidecar))();
                switch (nextItemRecord.Type) {
                    case ObjectTypes.TOPIC:
                        actions.push(
                            FullscreenActions.openTopic({
                                recordGuid: nextItemRecord.Guid,
                                recordType: ObjectTypes.TOPIC,
                                isMetadata: isMetadataTopicNavigation,
                            })
                        );
                        if (selectedAssignment) {
                            actions.push(SidecarEfffectActions.clearSelectedAssignment({ sidecarType: sidecar }));
                        }
                        // BIG TODO: A lot of edge cases where we don't have main sidecar opened, or assigments are opened in some other sidecar, or we have stories opened, etc...
                        break;
                    case ObjectTypes.ASSIGNMENT: {
                        const assignmentTopicGuid = (nextItemRecord as StorytellingAssignmentRecord).ParentGuid || nextItemRecord.Path?.parentCollectionGuid;
                        actions.push(FullscreenActions.openTopic({ recordGuid: assignmentTopicGuid, recordType: ObjectTypes.TOPIC, isMetadata: true }));
                        actions.push(MainSidecarActions.selectSidecarAssignment({ assignmentGuid: nextItemRecord.Guid }));
                        actions.push(MainSidecarActions.selectSidecarMenu({ menu: SidecarMenu.ASSIGNMENTS }));
                        if (currentTemplate === FullscreenLayoutTemplate.ONLY_MAIN) {
                            actions.push(FullscreenActions.setFullscreenTemplate({ template: FullscreenLayoutTemplate.BASIC }));
                        }
                        break;
                    }
                    case ObjectTypes.SCRIPT:
                        //We enter here because throughtTopicStories is false and passing standaloneStory true we consider its coming from list of stories.
                        actions.push(FullscreenActions.tryOpenStory({ recordGuid: nextItemRecord.Guid, recordType: ObjectTypes.SCRIPT, standaloneStory: true }));
                        break;
                    case ObjectTypes.RUNDOWN: {
                        const sidecarStates = this.store.selectSignal(selectAllSidecarStates)();
                        const sidecarsToClear = Object.entries(sidecarStates).reduce((acc, [sidecar, state]) => {
                            const shouldClearOpenedStory = !!state.selectedStoryGuid && state.currentStoryViewStateGuid === SYSTEM_VS_GUID;
                            if (shouldClearOpenedStory) {
                                acc[sidecar] = state;
                            }
                            return acc;
                        }, {} as SidecarStatesMap);
                        if (Object.keys(sidecarsToClear).length > 0) {
                            actions.push(...cleanupStoryFromSidecarActions(sidecarsToClear, null));
                        }
                        // when we navigate rundowns, it could be either from live or list of RO, or we could be in manage RO templates. So if it is a template we are navigating through the templates list.
                        if (nextItemRecord.Path.isTemplate()) {
                            actions.push(FullscreenActions.openRundownTemplate({ recordGuid: nextItemRecord.Guid, recordType: ObjectTypes.RUNDOWN, fromPinnedItem: false, standaloneStory: false }));
                        } else {
                            actions.push(FullscreenActions.openRundown({ recordGuid: nextItemRecord.Guid, recordType: ObjectTypes.RUNDOWN, fromPinnedItem: false, standaloneStory: false }));
                        }
                        break;
                    }
                    default:
                        break;
                }
                return actions;
            }
            return of(ApplicationActions.noOpAction());
        };
        return localNavigateToRecord(nextItem);
    }

    reloadSidecarItemsForTheCurrentRecord$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                OpenFullscreenModalActions.openRundownFullscreenDialog,
                OpenFullscreenModalActions.openRundownTemplateFullscreenDialog,
                OpenFullscreenModalActions.openStoryFullscreenDialog,
                OpenFullscreenModalActions.openTopicFullscreenDialog,
                FullscreenActions.openTopic,
                FullscreenActions.openStory,
                FullscreenActions.openRundown,
                FullscreenActions.openRundownTemplate
            ),
            concatLatestFrom(() => {
                return [
                    this.store.select(selectSidecarMainCurrentStoryViewState),
                    this.store.select(selectSidecarSecondCurrentStoryViewState),
                    this.store.select(selectSidecarThirdCurrentStoryViewState),
                    this.store.select(selectSidecarFourthCurrentStoryViewState),
                    this.store.select(selectSidecarMainCurrentAssignmentViewState),
                    this.store.select(selectSidecarSecondCurrentAssignmentViewState),
                    this.store.select(selectSidecarThirdCurrentAssignmentViewState),
                    this.store.select(selectSidecarFourthCurrentAssignmentViewState),
                    this.store.select(selectSidecarMainCurrentStoryListSearchType),
                    this.store.select(selectSidecarSecondCurrentStoryListSearchType),
                    this.store.select(selectSidecarThirdCurrentStoryListSearchType),
                    this.store.select(selectSidecarFourthCurrentStoryListSearchType),
                ];
            }),
            exhaustMap(
                ([
                    _,
                    mainStoryVS,
                    secondStoryVS,
                    thirdStoryVS,
                    fourthStoryVS,
                    mainAssignmentVS,
                    secondAssignmentVS,
                    thirdAssignmentVS,
                    fourthAssignmentVS,
                    mainStorySearchType,
                    secondStorySearchType,
                    thirdStorySearchType,
                    fourthStorySearchType,
                ]) => {
                    const actions = [];
                    if (mainStoryVS?.Guid === SYSTEM_VS_GUID && mainStorySearchType !== SearchType.SEARCH) {
                        actions.push(SidecarEfffectActions.reloadSidecarStoryBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_MAIN }));
                    } else if (mainStoryVS?.Guid === SUBSTORIES_VS_GUID) {
                        actions.push(SidecarEfffectActions.setCurrentSubStories({ sidecarType: FullscreenLayoutAreas.SIDECAR_MAIN }));
                    }
                    if (secondStoryVS?.Guid === SYSTEM_VS_GUID && secondStorySearchType !== SearchType.SEARCH) {
                        actions.push(SidecarEfffectActions.reloadSidecarStoryBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_SECOND }));
                    } else if (secondStoryVS?.Guid === SUBSTORIES_VS_GUID) {
                        actions.push(SidecarEfffectActions.setCurrentSubStories({ sidecarType: FullscreenLayoutAreas.SIDECAR_SECOND }));
                    }
                    if (thirdStoryVS?.Guid === SYSTEM_VS_GUID && thirdStorySearchType !== SearchType.SEARCH) {
                        actions.push(SidecarEfffectActions.reloadSidecarStoryBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_THIRD }));
                    } else if (thirdStoryVS?.Guid === SUBSTORIES_VS_GUID) {
                        actions.push(SidecarEfffectActions.setCurrentSubStories({ sidecarType: FullscreenLayoutAreas.SIDECAR_THIRD }));
                    }
                    if (fourthStoryVS?.Guid === SYSTEM_VS_GUID && fourthStorySearchType !== SearchType.SEARCH) {
                        actions.push(SidecarEfffectActions.reloadSidecarStoryBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_FOURTH }));
                    } else if (fourthStoryVS?.Guid === SUBSTORIES_VS_GUID) {
                        actions.push(SidecarEfffectActions.setCurrentSubStories({ sidecarType: FullscreenLayoutAreas.SIDECAR_FOURTH }));
                    }
                    if (mainAssignmentVS?.Guid === SYSTEM_VS_GUID) {
                        actions.push(SidecarEfffectActions.reloadSidecarAssignmentBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_MAIN }));
                    }
                    if (secondAssignmentVS?.Guid === SYSTEM_VS_GUID) {
                        actions.push(SidecarEfffectActions.reloadSidecarAssignmentBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_SECOND }));
                    }
                    if (thirdAssignmentVS?.Guid === SYSTEM_VS_GUID) {
                        actions.push(SidecarEfffectActions.reloadSidecarAssignmentBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_THIRD }));
                    }
                    if (fourthAssignmentVS?.Guid === SYSTEM_VS_GUID) {
                        actions.push(SidecarEfffectActions.reloadSidecarAssignmentBasedOnCurrentViewState({ sidecarType: FullscreenLayoutAreas.SIDECAR_FOURTH }));
                    }
                    if (actions.length > 0) {
                        return actions;
                    }
                    return of(ApplicationActions.noOpAction());
                }
            )
        );
    });

    openMediaFullscreen$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(OpenFullscreenModalActions.setFullscreenMediaUrls),
                map(() => {
                    this.dialog.open(MediaFullscreenComponent, {
                        id: 'media-fullscreen',
                        height: '100%',
                        width: '100%',
                        hasBackdrop: true,
                        disableClose: false,
                        panelClass: ['st-media-fullscreen'],
                    });
                })
            );
        },
        { dispatch: false }
    );

    navigateFullscreenMedia$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenFullscreenModalActions.navigateFullscreenMedia),
            map(action => {
                const mediaUrls = this.store.selectSignal(selectFullscreenMediaUrls);
                const currentMediaUrl = this.store.selectSignal(selectCurrentFullscreenMediaUrl);
                const nextItem = navigateToImage(action.direction, currentMediaUrl(), mediaUrls());
                return OpenFullscreenModalActions.setFullscreenCurrentMediaUrl({ mediaUrl: nextItem });
            })
        );
    });

    applyItemLayoutFromHello$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(HelloResultsActions.processHelloResults),
            map(({ helloResults }) => {
                const raw = helloResults?.ItemLayoutState;
                let layoutState: Partial<ItemLayoutState> = {};
                if (typeof raw === 'string' && raw.length) {
                    try {
                        layoutState = JSON.parse(raw);
                    } catch (err) {
                        console.warn('[ItemLayout] failed to parse ItemLayoutState from HelloResults', err);
                    }
                }
                return FullscreenActions.applyItemLayoutState({ state: layoutState });
            })
        );
    });

    saveItemLayout$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(FullscreenActions.setItemLayoutState),
                concatLatestFrom(() => this.store.select(selectItemLayoutState)),
                mergeMap(([, layoutState]) => {
                    const stringified = JSON.stringify(layoutState);
                    return from(this.personalSettingsService.updatePersonalSettings({ ItemLayoutState: stringified })).pipe(
                        map(response => {
                            if (response?.status) {
                                const helloResults = this.nomWebServicesJson.HelloResults;
                                if (helloResults) {
                                    helloResults.ItemLayoutState = stringified;
                                    localStorage.setItem('GlobalsHelloResults', JSON.stringify(helloResults));
                                }
                            }
                            return response;
                        }),
                        catchError(err => {
                            console.warn('[ItemLayout] saveItemLayout failed', err);
                            return of(null);
                        })
                    );
                })
            );
        },
        { dispatch: false }
    );
}
